import React from 'react';
import css from './PremiumProfileBio.module.css';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faLanguage,
  faMapMarkerAlt,
  faCalendarAlt,
  faCircle,
} from '@fortawesome/free-solid-svg-icons';
import ReactHtmlParser from 'react-html-parser';
import { decode } from 'html-entities';
import { Avatar } from '../../../components';
import TierBadge from '../TierBadge/TierBadge';
import ShareButton from '../share-button/ShareButton';
import { TIER_META } from '../../../util/coachTier';

// Strips markup/entities down to plain, whitespace-collapsed text — used to
// derive the hero's short blurb from the coach's rich-text description.
function stripHtml(html) {
  if (!html) return '';
  return decode(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncate(text, maxLen) {
  if (!text || text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim() + '…';
}

export default function PremiumProfileBio(props) {
  const {
    title,
    bio,
    services = [],
    authorName,
    language = [],
    location = '',
    sessions = 6,
    durationLabel = '',
    user,
    authorUrl,
    heroHighlights: heroHighlightsProp = [],
    coachTier,
    rightSlot,
    listingId,
  } = props;

  // Only show the coach's own publicData.outcomes — no placeholder fallback.
  const heroHighlights = (heroHighlightsProp || []).filter(
    item => typeof item === 'string' && item.trim() !== ''
  );
  const coachTierMeta = coachTier ? TIER_META[coachTier] : null;

  const firstLanguage = language.filter(l => l !== '')[0] || 'English';
  const locationShort = location
    ? location
        .split(',')
        .slice(0, 2)
        .join(',')
        .trim()
    : 'Remote';

  const filteredServices = services.filter(s => s !== '');
  const eyebrow = filteredServices[0] || null;

  const bioPlain = stripHtml(bio);
  const heroDescription = bioPlain ? truncate(bioPlain, 220) : '';

  const durationSessions = [durationLabel, sessions ? `${sessions} sessions` : '']
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={css.base}>
      {/* ── Hero ── */}
      <div className={css.heroCard}>
        <div className={css.heroLayout}>
          <div className={css.heroMain}>
            <div className={css.heroTopRow}>
              {eyebrow ? <span className={css.eyebrow}>{eyebrow}</span> : <span />}
              <ShareButton
                title={authorName ? `${title} with ${authorName}` : title}
                text={heroDescription}
                listingId={listingId}
                syncAddressBar
              />
            </div>

            <h1 className={css.heroTitle}>{title}</h1>

            {heroDescription && <p className={css.heroDescription}>{heroDescription}</p>}

            {authorName && (
              <div className={css.heroCoachRow}>
                {user && (
                  authorUrl ? (
                    <Link to={authorUrl}>
                      <Avatar disableProfileLink user={user} className={css.heroCoachAvatar} />
                    </Link>
                  ) : (
                    <Avatar disableProfileLink user={user} className={css.heroCoachAvatar} />
                  )
                )}
                <span className={css.heroCoachText}>
                  With{' '}
                  {authorUrl ? (
                    <Link to={authorUrl} className={css.heroCoachName}>
                      {authorName}
                    </Link>
                  ) : (
                    <span className={css.heroCoachName}>{authorName}</span>
                  )}
                </span>
                {coachTierMeta && coachTierMeta.showBadge && (
                  <span className={css.heroCoachBadge}>
                    <TierBadge tier={coachTier} size="sm" />
                    {coachTierMeta.label}
                  </span>
                )}
              </div>
            )}

            {heroHighlights.length > 0 && (
              <ul className={css.heroHighlights}>
                {heroHighlights.map((item, i) => (
                  <li key={i} className={css.heroHighlightItem}>
                    <FontAwesomeIcon icon={faCircle} className={css.heroHighlightIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}

            <p className={css.heroMetaRow}>
              {durationSessions && (
                <span className={css.heroMetaItem}>
                  <FontAwesomeIcon icon={faCalendarAlt} className={css.heroMetaIcon} />
                  {durationSessions}
                </span>
              )}
              {location && (
                <>
                  {durationSessions && <span className={css.heroMetaDot}>·</span>}
                  <span className={css.heroMetaItem}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className={css.heroMetaIcon} />
                    {locationShort}
                  </span>
                </>
              )}
              {firstLanguage && (
                <>
                  {(durationSessions || location) && <span className={css.heroMetaDot}>·</span>}
                  <span className={css.heroMetaItem}>
                    <FontAwesomeIcon icon={faLanguage} className={css.heroMetaIcon} />
                    {firstLanguage}
                  </span>
                </>
              )}
            </p>
          </div>

          {rightSlot && <div className={css.heroBookingCol}>{rightSlot}</div>}
        </div>
      </div>

      {/* ── About this program ── */}
      <div className={css.aboutSection}>
        <h2 className={css.sectionHeading}>
          <FontAwesomeIcon icon={faFileAlt} className={css.sectionHeadingIcon} />
          About this program
        </h2>

        <div className={[css.bio, 'ql-editor'].join(' ')}>{ReactHtmlParser(decode(bio))}</div>
      </div>
    </div>
  );
}
